<template>
  <!-- Table Container Card -->
  <b-card no-body>
    <div class="m-2">
      <!-- Table Top -->
      <b-row>
        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />

          <b-dropdown
            v-if="getUserPermission('VIEW_INWARD_TRANSFER_REPORT')"
            id="dropdown-1"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            :text="$t('Export')"
            variant="primary"
            :disabled="isDisabled"
          >
            <b-dropdown-item @click="csvExport">
              {{ $t('CSV') }}
            </b-dropdown-item>
            <b-dropdown-item @click="excelExport">
              {{ $t('Excel') }}
            </b-dropdown-item>
          </b-dropdown>
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <!-- Dropdown -->
            <Button
              v-if="getUserPermission('POST_INWARD_WITHOUT_GATEWAY_CHECK') && selectedPendingIds.length !== 0"
              :label="$t('Process Pending Request')"
              size="md"
              class="mr-1"
              @click="onProcessPendingRequest"
            />

            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>
    </div>

    <b-table
      ref="customerInfoRef"
      :items="inwardTransferList"
      responsive
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>

      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>
      <template #cell(select)="data">
        <b-form-group class="mt-2">
          <b-form-checkbox
            v-if="data.item.status === 'Pending'"
            v-model="selectedPendingIds"
            :value="data.item.id"
          />
        </b-form-group>
      </template>
      <template #cell(dateCreated)="data">
        {{ toDateWithCustomTime(data.value) }}
      </template>
      <template #cell(isAwaitingReversal)="data">
        <b-badge
          pill
          :variant="getStatusColor(data.value ? 'Yes' : 'No')"
        >
          {{ data.value ? $t('Yes') : $t('No') }}
        </b-badge>
      </template>
      <template #cell(isReversed)="data">
        <b-badge
          pill
          :variant="getStatusColor(data.value ? 'Yes' : 'No')"
        >
          {{ data.value ? $t('Yes') : $t('No') }}
        </b-badge>
      </template>
      <template #cell(status)="data">
        <b-badge
          v-if="data.value"
          pill
          :variant="getStatusColor(data.value)"
        >
          {{ data.value }}
        </b-badge>
      </template>
    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />
    <!-- Filter modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      no-close-on-backdrop
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      :title="$t('Filters')"
      modal-footer
      @ok="fetchInwardTransfer"
      @cancel="resetFilters"
    >
      <b-row class="match-height">
        <!-- Account Name -->
        <b-col md="6">
          <InputField
            id="account-name"
            v-model="filter.accountName"
            :label="$t('Originator Account Name')"
            :placeholder="$t('Enter account name')"
            type="text"
            :is-allow-special-characters="false"
          />
        </b-col>
        <!-- Account Name -->

        <!-- Account Number -->
        <b-col md="6">
          <InputField
            id="account-number"
            v-model="filter.accountNumber"
            :label="$t('Originator Account Number')"
            :placeholder="$t('Enter account number')"
            type="number"
          />
        </b-col>
        <!-- Account Number -->

        <!-- Beneficiary Account Number -->
        <b-col md="6">
          <InputField
            id="beneficiary-account-number"
            v-model="filter.beneficiaryAccountNumber"
            :label="$t('Beneficiary Account Number')"
            :placeholder="$t('Enter Beneficiary Account Number')"
            type="number"
          />
        </b-col>
        <!-- Beneficiary Account Number -->

        <b-col md="6">
          <InputField
            id="session-id"
            v-model="filter.sessionID"
            :label="$t('Session ID')"
            :placeholder="$t('Enter Session ID')"
            type="text"
          />
        </b-col>
        <b-col md="6">
          <InputField
            id="posting-reference"
            v-model="filter.postingsReference"
            :label="$t('Posting Reference')"
            :placeholder="$t('Enter Posting Reference')"
            type="text"
          />
        </b-col>
        <b-col md="6">
          <InputField
            id="payment-reference"
            v-model="filter.paymentReference"
            :label="$t('Payment Reference')"
            :placeholder="$t('Enter Payment Reference')"
            type="text"
          />
        </b-col>
        <b-col md="6">
          <InputField
            id="name-enquiry-ref"
            v-model="filter.nameEnquiryRef"
            :label="$t('Name Enquiry Reference')"
            :placeholder="$t('Enter Name Enquiry Reference')"
            type="text"
          />
        </b-col>
        <b-col md="6">
          <SelectField
            id="gateway-provider"
            v-model="filter.gateway"
            :label="$t('Gateway')"
            :options="gatewayProviderOptions"
            item="label"
            :reduce="option => option"
            :placeholder="$t('Select')"
          />
        </b-col>
        <b-col md="6">
          <DatePickerField
            id="transaction-date-from"
            v-model="filter.transactionDateFrom"
            :placeholder="$t('Select date')"
            :label="$t('Transaction Date From')"
            :config="datePickerConfig"
          />
        </b-col>
        <b-col md="6">
          <DatePickerField
            id="transaction-date-to"
            v-model="filter.transactionDateTo"
            :placeholder="$t('Select date')"
            :label="$t('Transaction Date To')"
            :config="datePickerConfig"
          />
        </b-col>
        <b-col md="6">
          <InputField
            id="response-code"
            v-model="filter.responseCode"
            :label="$t('Response Code')"
            :placeholder="$t('Enter Response Code')"
            type="text"
          />
        </b-col>
        <b-col md="6">
          <SelectField
            id="is-awaiting-reversal"
            v-model="filter.isAwaitingReversal"
            :label="$t('Is Awaiting Reversal?')"
            :placeholder="$t('Select')"
            :options="isAwaitingReversalOptions"
            item="label"
            :reduce="option => option.value"
          />
        </b-col>
        <b-col md="6">
          <SelectField
            id="is-reversed"
            v-model="filter.isReversed"
            :label="$t('Is Reversed?')"
            :placeholder="$t('Select')"
            :options="isReversedOptions"
            item="label"
            :reduce="option => option.value"
          />
        </b-col>
        <!-- Status -->
        <b-col md="6">
          <SelectField
            id="status"
            v-model="filter.status"
            :label="$t('Status')"
            :placeholder="$t('Select')"
            :options="translatedStatusOptions"
            item="label"
            :reduce="option => option.value"
          />
        </b-col>
        <!-- Status -->
      </b-row>
    </b-modal>
    <!-- Filter modal -->

    <FullScreenSpinner :show="showSpinner" />
  </b-card>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BTable,
  BDropdown,
  BDropdownItem,
  BSpinner,
  BBadge,
  BFormGroup,
  BFormCheckbox,
} from 'bootstrap-vue'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { permissionMixin } from '@core/mixins/permission'
import { mapActions } from 'vuex'
import * as FileSaver from 'file-saver'
import TablePagination from '@/views/components/table/TablePagination.vue'
import getStatusColor from '../../../../../helpers/getStatusColor'
import { toAmount, toBalance, toFormatAmount } from '@/helpers/formatAmount'
import TableEntries from '@/views/components/table/TableEntries.vue'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import DatePickerField from '../../../../forms/DatePickerField.vue'
import {
  toDateWithYear,
  toDateWithTime,
  toDateTimeFormat,
  toDateWithCustomTime,
} from '../../../../../helpers/formatDate'
import InputField from '../../../../forms/InputField.vue'
import SelectField from '../../../../forms/SelectField.vue'
import Button from '../../../../components/button/Button.vue'
import { eChannelsPermission } from '../../../../../helpers/permissions'

const statusOptions = [
  { label: 'Processed', value: 'Processed' },
  { label: 'Pending', value: 'Pending' },
  { label: 'Failed', value: 'Failed' },
  { label: 'Reversed', value: 'Reversed' },
]

const options = [
  { label: 'Yes', value: 'Yes' },
  { label: 'No', value: 'No' },
]

const getDefaultFilter = () => ({
  accountName: null,
  accountNumber: null,
  beneficiaryAccountNumber: null,
  status: null,
  transactionDateFrom: null,
  transactionDateTo: null,
  paymentReference: null,
  postingsReference: null,
  responseCode: null,
  nameEnquiryRef: null,
  sessionID: null,
  isAwaitingReversal: null,
  isReversed: null,
  gateway: null,
})

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BDropdown,
    BDropdownItem,
    Button,
    BSpinner,
    BTable,
    BBadge,
    BFormGroup,
    BFormCheckbox,
    TablePagination,
    TableEntries,
    InputField,
    SelectField,
    FullScreenSpinner,
    DatePickerField,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      toDateWithYear,
      toDateWithTime,
      toDateTimeFormat,
      toDateWithCustomTime,
      getStatusColor,
      toAmount,
      toFormatAmount,
      toBalance,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'select', label: 'Select' },
        { key: 'originatorAccountName', sortable: true, label: 'Originator Account Name' },
        { key: 'originatorAccountNumber', sortable: true, label: 'Originator Account Number' },
        { key: 'originatorBankVerificationNumber', sortable: true, label: 'Originator BVN' },
        { key: 'originatorKYCLevel', sortable: true, label: 'Originator KYC Level' },
        { key: 'originatorBankName', sortable: true, label: 'Originator Bank Name' },
        { key: 'beneficiaryAccountNumber', sortable: true, label: 'Beneficiary Account No.' },
        { key: 'beneficiaryAccountName', sortable: true, label: 'Beneficiary Account Name' },
        { key: 'beneficiaryBankVerificationNumber', sortable: true, label: 'Beneficiary BVN' },
        { key: 'amount', sortable: true, label: 'Amount' },
        { key: 'destinationInstitutionCode', sortable: true, label: 'Destination Institution Code' },
        { key: 'sessionID', sortable: true, label: 'Session ID' },
        { key: 'postingsReference', sortable: true, label: 'Posting Reference' },
        { key: 'nameEnquiryRef', sortable: true, label: 'Name Enquiry Reference' },
        { key: 'originatorNarration', sortable: true, label: 'Originator Narration' },
        { key: 'paymentReference', sortable: true, label: 'Payment Reference' },
        { key: 'responseCode', sortable: true, label: 'Response Code' },
        { key: 'gateway', sortable: true, label: 'Transfer Gateway' },
        { key: 'tsqResponseCode', sortable: true, label: 'TSQ Response Code' },
        { key: 'retryCount', sortable: true, label: 'Retry Count' },
        { key: 'isAwaitingReversal', sortable: true, label: 'Is Awaiting Reversal?' },
        { key: 'isReversed', sortable: true, label: 'Is Reversed?' },
        { key: 'dateCreated', sortable: true, label: 'Transaction Date' },
        { key: 'status', sortable: true, label: 'Status' },
      ],
      inwardTransferList: [],
      selectedPendingIds: [],
      filter: getDefaultFilter(),
      datePickerConfig: {
        dateFormat: 'Y-m-d',
      },
      // isAwaitingReversalOptions: ['Yes', 'No'],
      // isReversedOptions: ['Yes', 'No'],
      showSpinner: false,
      gatewayProviderOptions: [],
    }
  },
  computed: {
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedStatusOptions() {
      return statusOptions.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
    isReversedOptions() {
      return options.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
    isAwaitingReversalOptions() {
      return options.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchInwardTransfer()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchInwardTransfer()
    },
  },
  mounted() {
    this.fetchInwardTransfer()
    this.fetchGatewayProviders()
  },
  methods: {
    ...mapActions({
      getInwardTransfers: 'eChannels/getInwardTransfers',
      downloadInwardTransactionLogs: 'eChannels/downloadInwardTransactionLogs',
      getGatewayProviders: 'eChannels/getGatewayProviders',
      performPendingRequestAction: 'eChannels/performPendingRequestAction',
    }),

    getSearchFilterPayload() {
      const mapYesNoToBoolean = value => {
        if (value === 'Yes') return true
        if (value === 'No') return false
        return null
      }

      return {
        ...this.filter,
        isAwaitingReversal: mapYesNoToBoolean(this.filter.isAwaitingReversal),
        isReversed: mapYesNoToBoolean(this.filter.isReversed),
      }
    },

    onProcessPendingRequest() {
      const permission = 'POST_INWARD_WITHOUT_GATEWAY_CHECK'

      const performPendingRequest = async () => {
        try {
          this.showSpinner = true
          const formData = {
            ids: this.selectedPendingIds.toString(),
            isSessionIDPassed: false,
          }
          const response = await this.performPendingRequestAction({
            formData,
            permission,
          })
          if (response.status === 200) {
            this.fetchInwardTransfer()
            this.selectedPendingIds = []
            this.alertSuccess({
              text: this.$t(
                'Your request to credit the selected accounts has been recieved and will be processed shortly.',
              ),
            })
            this.showSpinner = false
          }
        } catch (error) {
          this.handleError(error)
        } finally {
          this.showSpinner = false
        }
      }
      this.confirm(performPendingRequest, {
        title: '',
        text: this.$t(
          'You are about to credit the selected accounts without verifying the current transaction status from the gateway. Are you sure you want continue?',
        ),
      })
    },
    async fetchInwardTransfer() {
      try {
        this.isBusy = true
        const response = await this.getInwardTransfers({
          search: this.getSearchFilterPayload(),
          page: this.currentPage,
          limit: this.perPage,
          permission: eChannelsPermission.VIEW_INWARD_TRANSFER_REPORT,
        })
        const { status, data } = response
        if (status === 200) {
          this.isBusy = false
          this.inwardTransferList = data.data
            ? data.data.map(item => ({
              ...item,
              amount: toBalance(item.amount),
              fee: toBalance(item.fee),
            }))
            : []
          this.totalCount = data.recordCount
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async fetchGatewayProviders() {
      try {
        const response = await this.getGatewayProviders({ permission: eChannelsPermission.VIEW_INWARD_TRANSFER_REPORT })
        const { status, data } = await response
        if (status === 200 && data) {
          this.gatewayProviderOptions = data
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    async exportInwardTransfers(fileExportFormat) {
      try {
        this.showSpinner = true
        const response = await this.downloadInwardTransactionLogs({
          search: this.getSearchFilterPayload(),
          permission: eChannelsPermission.VIEW_INWARD_TRANSFER_REPORT,
          fileExportFormat,
        })

        if (response.status === 200) {
          // Determine file type
          let fileType
          let fileExtension

          if (fileExportFormat === 2) {
            fileType = 'text/csv'
            fileExtension = 'csv'
          }

          if (fileExportFormat === 1) {
            fileType = 'application/vnd.ms-excel'
            fileExtension = 'xlsx'
          }
          const fileLabel = fileExtension === 'csv' ? 'CSV' : 'Excel'

          const blob = new Blob([response.data], { type: fileType })
          FileSaver.saveAs(blob, `Inward_Transfer_Reports_${new Date().toISOString().slice(0, 10)}.${fileExtension}`)
          this.alertSuccess({ text: this.$t(`Your request to download Inward Transfer Report as ${fileLabel} was successful`) })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.showSpinner = false
      }
    },
    async csvExport() {
      await this.exportInwardTransfers(2)
    },
    async excelExport() {
      await this.exportInwardTransfers(1)
    },
    resetFilters() {
      this.filter = getDefaultFilter()
    },
    resetFilter() {
      this.filter = getDefaultFilter()
      this.selectedPendingIds = []
      this.fetchInwardTransfer()
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
